import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { OWN_ERROR_TOAST } from 'src/app/shared/interceptors/http-error.interceptor';
import { BonusQuote, BonusTier, BonusTierList } from 'src/app/models/WalletBonus';

/**
 * Bônus de recarga (CU-86akn0zp6).
 *
 * ## Por que não está no `WalletPricingService`
 *
 * As duas pontas ficam em rotas diferentes e com semânticas diferentes: a tabela
 * de faixas é administrativa (`/admin/wallet/bonus-tiers`) e a cotação é do
 * fluxo de recarga (`/recharges/bonus-quote`, que o admin chama com `simulate`).
 * E, ao contrário das rotas de preço, a tabela tem **PUT**: ela é uma
 * configuração substituída inteira, não uma série de vigências append-only.
 * Juntar as duas coisas na mesma classe apagaria justamente essa diferença.
 */
@Injectable({ providedIn: 'root' })
export class WalletBonusService {

  constructor(private http: HttpClient) {}

  /** A tabela inteira, em ordem crescente de valor. */
  listTiers(): Observable<BonusTierList> {
    return this.http.get<BonusTierList>(`${environment.apiUrl}/admin/wallet/bonus-tiers`);
  }

  /**
   * Substitui a tabela inteira.
   *
   * Não há "adicionar faixa" nem "apagar faixa" no contrato: o corpo é a lista
   * final. Uma rota por linha deixaria a tabela em estado intermediário se uma
   * das requisições falhasse — e o bônus é lido por quem está recarregando
   * naquele instante. A rota exige `confirmed: true` (front-contrato-r3 §1): sem ele volta
   * 422 `errors.confirmed` e nada é gravado — quem chama passa pelo modal de confirmação antes.
   */
  saveTiers(tiers: BonusTier[]): Observable<BonusTierList> {
    return this.http.put<BonusTierList>(
      `${environment.apiUrl}/admin/wallet/bonus-tiers`,
      { tiers, confirmed: true },
      { context: new HttpContext().set(OWN_ERROR_TOAST, true) }
    );
  }

  /**
   * Quanto de bônus aquele valor renderia.
   *
   * `total` vai como **string decimal** porque é assim que o valor sai do campo
   * (e é o que a rota espera); `simulate` é o cliente para quem se pergunta — sem
   * ele a resposta seria sobre ninguém, e as exclusões por preço de assinante ou
   * de conta não teriam como ser avaliadas.
   *
   * `adminForced`: o admin marcou "dar bônus" numa conta que não recebe bônus
   * (CU-17tm466tzkz) — a prévia mostra a faixa que a recarga forçada vai pagar.
   * O backend só honra o parâmetro para administrador.
   */
  quote(total: string, simulateUserId?: string | null, adminForced = false): Observable<BonusQuote> {
    let params = new HttpParams().set('total', String(total));

    if (simulateUserId) {
      params = params.set('simulate', String(simulateUserId));
    }

    if (adminForced) {
      params = params.set('admin_forced', '1');
    }

    return this.http.get<BonusQuote>(`${environment.apiUrl}/recharges/bonus-quote`, { params });
  }
}
